/**
 * Collection filter tabs and color category tiles with combined keyword filtering.
 */
(function () {
  const SELECTORS = {
    section: '[data-collection-keyword-filter]',
    tab: '[data-collection-filter-tab]',
    colorTile: '[data-collection-color-tile]',
    colorTileList: '.collection-color-tiles__list',
    productList: 'product-list',
    productCard: 'product-card',
    productCount: '[data-collection-filter-count]',
    paginationContainer: '[data-collection-filter-pagination-container]',
    results: '.collection__results',
    emptyState: '[data-collection-filter-empty]',
    emptyMessage: '[data-collection-filter-empty-message]',
    clearFilter: '.collection-filter-clear-btn',
    clearFilterChip: '[data-collection-filter-clear-chip]',
  };

  const boundShopifySections = new WeakSet();
  const COLOR_TILE_DRAG_THRESHOLD = 5;
  let documentClicksBound = false;
  let colorTilePointer = {
    tracking: false,
    startX: 0,
    startY: 0,
    dragged: false,
  };

  function getRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  function getShopifySection(collection) {
    return collection.closest('.shopify-section');
  }

  /**
   * Resolve the collection for a filter control. Mobile filter-tab popovers are
   * appended to document.body while open, so they are no longer inside .collection.
   */
  function getCollectionForFilterControl(element) {
    if (!element) {
      return null;
    }

    const inCollection = element.closest(SELECTORS.section);

    if (inCollection) {
      return inCollection;
    }

    const popover = element.closest('x-popover');

    if (!popover?.id) {
      return null;
    }

    const control = document.querySelector(`[aria-controls="${popover.id}"]`);

    return control?.closest(SELECTORS.section) || null;
  }

  async function clearAllFilters(collection, state) {
    const tabs = Array.from(collection.querySelectorAll(SELECTORS.tab));
    const defaultTab = tabs.length ? getDefaultTab(tabs) : null;
    const defaultColorTile = getDefaultColorTile(Array.from(collection.querySelectorAll(SELECTORS.colorTile)));

    state.activeTabKey = '__all__';
    state.activeColorKey = '__all__';

    if (defaultTab) {
      state.activeTabKey = getTabKey(defaultTab);
    }

    if (defaultColorTile) {
      state.activeColorKey = getColorTileKey(defaultColorTile);
    }

    await renderCurrentView(collection, state, 1);
    scrollToCollection(collection);
  }

  function closeFilterTabPopover(tab) {
    const popover = tab.closest('x-popover');

    if (popover && typeof popover.hide === 'function') {
      popover.hide();
    }
  }

  function createPopoverAnimationControls(animations) {
    const finished = Promise.all(
      animations.map((animation) => animation.finished.catch(() => undefined))
    );

    return {
      finished,
      then(onFulfilled, onRejected) {
        return finished.then(onFulfilled, onRejected);
      },
      complete() {
        animations.forEach((animation) => {
          try {
            animation.finish();
          } catch (error) {
            /* animation may already be finished */
          }
        });
      },
    };
  }

  /**
   * Centered mobile popup should fade/scale instead of using the theme bottom-sheet slide,
   * which looks abrupt on close.
   */
  function enhanceFilterTabsPopover(popover) {
    if (!popover || !popover.classList.contains('collection-filter-tabs-popover') || popover._centeredAnimBound) {
      return;
    }

    popover._centeredAnimBound = true;

    const originalEnter = popover.createEnterAnimationControls.bind(popover);
    const originalLeave = popover.createLeaveAnimationControls.bind(popover);
    const isMobilePopup = () => window.matchMedia('screen and (max-width: 999px)').matches;
    const easing = 'cubic-bezier(0.645, 0.045, 0.355, 1)';

    popover.createEnterAnimationControls = function createEnterAnimationControls() {
      if (!isMobilePopup()) {
        return originalEnter();
      }

      const overlay = this.getShadowPartByName('overlay');
      const content = this.getShadowPartByName('content');

      content.style.cssText = '';

      return createPopoverAnimationControls([
        overlay.animate([{ opacity: 0 }, { opacity: 1 }], {
          duration: 280,
          easing,
          fill: 'both',
        }),
        content.animate(
          [
            { opacity: 0, transform: 'scale(0.96)' },
            { opacity: 1, transform: 'scale(1)' },
          ],
          {
            duration: 280,
            easing,
            fill: 'both',
          }
        ),
      ]);
    };

    popover.createLeaveAnimationControls = function createLeaveAnimationControls() {
      if (!isMobilePopup()) {
        return originalLeave();
      }

      const overlay = this.getShadowPartByName('overlay');
      const content = this.getShadowPartByName('content');

      return createPopoverAnimationControls([
        overlay.animate([{ opacity: 1 }, { opacity: 0 }], {
          duration: 220,
          easing,
          fill: 'both',
        }),
        content.animate(
          [
            { opacity: 1, transform: 'scale(1)' },
            { opacity: 0, transform: 'scale(0.96)' },
          ],
          {
            duration: 220,
            easing,
            fill: 'both',
          }
        ),
      ]);
    };
  }

  function enhanceFilterTabsPopovers(scope) {
    if (!scope) {
      return;
    }

    scope.querySelectorAll('.collection-filter-tabs-popover').forEach((popover) => {
      enhanceFilterTabsPopover(popover);
    });

    scope.querySelectorAll('[aria-controls]').forEach((control) => {
      const popover = document.getElementById(control.getAttribute('aria-controls'));

      enhanceFilterTabsPopover(popover);
    });
  }

  function resetColorTilePointer() {
    colorTilePointer.tracking = false;
    colorTilePointer.dragged = false;
    colorTilePointer.startX = 0;
    colorTilePointer.startY = 0;
  }

  function isColorTileDragClick(event) {
    if (event.detail === 0) {
      resetColorTilePointer();
      return false;
    }

    return colorTilePointer.dragged;
  }

  /**
   * Track pointer movement on color tiles so a drag-to-scroll does not apply a filter.
   * Bound on document so it survives Shopify facet section replacements.
   */
  function bindColorTilePointerTracking() {
    document.addEventListener(
      'pointerdown',
      (event) => {
        if (!event.target.closest?.(SELECTORS.colorTile)) {
          resetColorTilePointer();
          return;
        }

        colorTilePointer.tracking = true;
        colorTilePointer.dragged = false;
        colorTilePointer.startX = event.clientX;
        colorTilePointer.startY = event.clientY;
      },
      true
    );

    document.addEventListener(
      'pointermove',
      (event) => {
        if (!colorTilePointer.tracking || colorTilePointer.dragged) {
          return;
        }

        const deltaX = Math.abs(event.clientX - colorTilePointer.startX);
        const deltaY = Math.abs(event.clientY - colorTilePointer.startY);

        if (deltaX > COLOR_TILE_DRAG_THRESHOLD || deltaY > COLOR_TILE_DRAG_THRESHOLD) {
          colorTilePointer.dragged = true;
        }
      },
      true
    );

    document.addEventListener(
      'pointercancel',
      () => {
        resetColorTilePointer();
      },
      true
    );
  }

  /**
   * Suppress filter clicks that follow a drag on the color tile carousel.
   * Drag scrolling itself is handled by theme allow-drag.
   */
  function enhanceColorTileScroller(list) {
    if (list._colorTileScrollBound) {
      return;
    }

    list._colorTileScrollBound = true;

    list.addEventListener(
      'click',
      (event) => {
        if (!isColorTileDragClick(event)) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        resetColorTilePointer();
      },
      true
    );

    list.addEventListener('dragstart', (event) => event.preventDefault());
  }

  function enhanceColorTileScrollers(scope) {
    if (!scope) {
      return;
    }

    scope.querySelectorAll(SELECTORS.colorTileList).forEach((list) => {
      enhanceColorTileScroller(list);
    });
  }

  function getState(collection) {
    const shopifySection = getShopifySection(collection);

    if (!shopifySection._collectionFilterState) {
      shopifySection._collectionFilterState = {
        catalog: null,
        catalogPromise: null,
        cardCache: new Map(),
        currentPage: 1,
        activeTabKey: '__all__',
        activeColorKey: '__all__',
        paginationPartsTemplate: null,
      };
    }

    return shopifySection._collectionFilterState;
  }

  function isCombinedFiltered(state) {
    return state.activeTabKey !== '__all__' || state.activeColorKey !== '__all__';
  }

  function setLoading(collection, isLoading) {
    if (isLoading) {
      collection.setAttribute('data-collection-filter-loading', '');
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
    } else {
      collection.removeAttribute('data-collection-filter-loading');
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }

  function isShowAllItem(element, headingSelector) {
    if (element.dataset.filterShowAll === 'true') {
      return true;
    }

    const keyword = (element.dataset.filterKeyword || '').trim();
    const heading = element.querySelector(headingSelector)?.textContent || element.textContent || '';

    return keyword === '' || heading.trim().toLowerCase().includes('all');
  }

  function getTabTitle(tab) {
    return (tab.textContent || '').trim();
  }

  function getColorTileTitle(tile) {
    return (tile.querySelector('[data-color-tile-heading]')?.textContent || tile.textContent || '').trim();
  }

  function getTabKey(tab) {
    if (isShowAllItem(tab)) {
      return '__all__';
    }

    return (tab.dataset.filterKeyword || '').trim();
  }

  function getColorTileKey(tile) {
    if (isShowAllItem(tile, '[data-color-tile-heading]')) {
      return '__all__';
    }

    return (tile.dataset.filterKeyword || '').trim();
  }

  function getDefaultTab(tabs) {
    return tabs.find((tab) => tab.dataset.filterShowAll === 'true') || tabs[0];
  }

  function getDefaultColorTile(tiles) {
    return tiles.find((tile) => tile.dataset.filterShowAll === 'true') || tiles[0];
  }

  /**
   * Collect filter tabs from the collection and any related mobile popover
   * (which is moved to document.body while open on mobile).
   */
  function getFilterTabs(collection) {
    const tabs = Array.from(collection.querySelectorAll(SELECTORS.tab));
    const seen = new Set(tabs);

    collection.querySelectorAll('[aria-controls]').forEach((control) => {
      const popover = document.getElementById(control.getAttribute('aria-controls'));

      if (!popover) {
        return;
      }

      popover.querySelectorAll(SELECTORS.tab).forEach((tab) => {
        if (!seen.has(tab)) {
          seen.add(tab);
          tabs.push(tab);
        }
      });
    });

    return tabs;
  }

  function findTabByKey(collection, tabKey) {
    const tabs = getFilterTabs(collection);

    if (!tabs.length) {
      return null;
    }

    if (tabKey === '__all__') {
      return getDefaultTab(tabs);
    }

    return tabs.find((tab) => (tab.dataset.filterKeyword || '').trim() === tabKey) || null;
  }

  function setTabActiveState(collection, tabKey) {
    getFilterTabs(collection).forEach((tab) => {
      const isActive = getTabKey(tab) === tabKey;

      tab.classList.toggle('link', isActive);
      tab.classList.toggle('link-reversed', !isActive);
      tab.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  function setColorTileActiveState(collection, colorKey) {
    const tiles = Array.from(collection.querySelectorAll(SELECTORS.colorTile));

    tiles.forEach((tile) => {
      const isActive = getColorTileKey(tile) === colorKey;

      tile.classList.toggle('is-active', isActive);
      tile.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  async function fetchAllCollectionProducts(handle) {
    const products = [];
    let page = 1;

    while (true) {
      const response = await fetch(`${getRoot()}collections/${handle}/products.json?limit=250&page=${page}`);

      if (!response.ok) {
        break;
      }

      const data = await response.json();

      if (!data.products?.length) {
        break;
      }

      products.push(...data.products);

      if (data.products.length < 250) {
        break;
      }

      page += 1;
    }

    return products;
  }

  async function preloadCatalog(collection, state) {
    if (state.catalog) {
      return state.catalog;
    }

    if (state.catalogPromise) {
      return state.catalogPromise;
    }

    const handle = collection.dataset.collectionHandle;

    state.catalogPromise = fetchAllCollectionProducts(handle).then((products) => {
      state.catalog = products.map((product) => ({
        handle: product.handle,
        title: product.title,
      }));

      return state.catalog;
    });

    return state.catalogPromise;
  }

  async function fetchCollectionSectionPage(collection, page) {
    const handle = collection.dataset.collectionHandle;
    const sectionId = collection.dataset.sectionId;
    const url = `${getRoot()}collections/${handle}?section_id=${sectionId}&page=${page}`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error('Failed to fetch collection page');
    }

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

    return {
      results: doc.querySelector(SELECTORS.results),
      pagination: doc.querySelector('.pagination'),
      colorTiles: doc.querySelector('.collection-color-tiles'),
    };
  }

  function getFilteredProducts(catalog, collection, state) {
    const tab = findTabByKey(collection, state.activeTabKey);
    let products = catalog;

    if (tab && !isShowAllItem(tab)) {
      const tabKeyword = (tab.dataset.filterKeyword || '').trim().toLowerCase();
      products = products.filter((product) => product.title.toLowerCase().includes(tabKeyword));
    }

    if (state.activeColorKey !== '__all__') {
      const colorKeyword = state.activeColorKey.toLowerCase();
      products = products.filter((product) => product.title.toLowerCase().includes(colorKeyword));
    }

    return products;
  }

  function getPerPage(collection) {
    return parseInt(collection.dataset.productsPerPage, 10) || 36;
  }

  function showSkeletons(productList, count) {
    productList.innerHTML = '';

    for (let index = 0; index < count; index += 1) {
      const skeleton = document.createElement('div');
      skeleton.className = 'collection-filter-skeleton-card';
      skeleton.setAttribute('aria-hidden', 'true');
      productList.appendChild(skeleton);
    }
  }

  async function fetchCardsForHandles(collection, state, catalog, handles) {
    const perPage = getPerPage(collection);
    const pagesToFetch = new Set();

    handles.forEach((handle) => {
      if (state.cardCache.has(handle)) {
        return;
      }

      const index = catalog.findIndex((product) => product.handle === handle);

      if (index >= 0) {
        pagesToFetch.add(Math.floor(index / perPage) + 1);
      }
    });

    for (const page of pagesToFetch) {
      const { results } = await fetchCollectionSectionPage(collection, page);

      results?.querySelectorAll(SELECTORS.productCard).forEach((card) => {
        const handle = card.getAttribute('handle');

        if (handle && !state.cardCache.has(handle)) {
          state.cardCache.set(handle, document.importNode(card, true));
        }
      });
    }

    return handles.map((handle) => state.cardCache.get(handle)).filter(Boolean);
  }

  function cacheInitialCards(collection, state) {
    const productList = collection.querySelector(SELECTORS.productList);

    if (!productList) {
      return;
    }

    productList.querySelectorAll(SELECTORS.productCard).forEach((card) => {
      const handle = card.getAttribute('handle');

      if (handle) {
        state.cardCache.set(handle, document.importNode(card, true));
      }
    });
  }

  function storePaginationTemplate(collection, state) {
    if (state.paginationPartsTemplate) {
      return;
    }

    const pagination = collection.querySelector('.pagination');

    if (!pagination) {
      return;
    }

    state.paginationPartsTemplate = {
      prev: pagination.querySelector('[rel="prev"]')?.closest('facet-link, a')?.outerHTML || '',
      next: pagination.querySelector('[rel="next"]')?.closest('facet-link, a')?.outerHTML || '',
    };
  }

  function getPaginationParts(currentPage, totalPages) {
    if (totalPages <= 1) {
      return [];
    }

    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => ({ type: 'page', page: index + 1 }));
    }

    const parts = [{ type: 'page', page: 1 }];

    if (currentPage > 3) {
      parts.push({ type: 'ellipsis' });
    }

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    for (let page = start; page <= end; page += 1) {
      parts.push({ type: 'page', page });
    }

    if (currentPage < totalPages - 2) {
      parts.push({ type: 'ellipsis' });
    }

    parts.push({ type: 'page', page: totalPages });

    return parts;
  }

  function renderClientPagination(collection, state, currentPage, totalPages) {
    const container = collection.querySelector(SELECTORS.paginationContainer);

    if (!container) {
      return;
    }

    if (totalPages <= 1) {
      container.innerHTML = '';
      return;
    }

    storePaginationTemplate(collection, state);

    const nav = document.createElement('nav');
    nav.className = 'pagination';
    nav.setAttribute('data-collection-filter-pagination', '');
    nav.setAttribute('role', 'navigation');

    if (currentPage > 1 && state.paginationPartsTemplate?.prev) {
      const previous = document.createElement('div');
      previous.innerHTML = state.paginationPartsTemplate.prev;
      const previousWrapper = previous.firstElementChild;

      if (previousWrapper) {
        const link = previousWrapper.querySelector('a') || previousWrapper;
        link.setAttribute('data-filter-page', String(currentPage - 1));
        link.setAttribute('href', '#');
        nav.appendChild(previousWrapper);
      }
    }

    getPaginationParts(currentPage, totalPages).forEach((part) => {
      if (part.type === 'ellipsis') {
        const ellipsis = document.createElement('span');
        ellipsis.className = 'pagination__link subheading';
        ellipsis.textContent = '…';
        nav.appendChild(ellipsis);
        return;
      }

      if (part.page === currentPage) {
        const current = document.createElement('span');
        current.className = 'pagination__link subheading';
        current.setAttribute('aria-current', 'page');
        current.textContent = String(part.page);
        nav.appendChild(current);
        return;
      }

      const link = document.createElement('a');
      link.className = 'pagination__link subheading';
      link.href = '#';
      link.textContent = String(part.page);
      link.setAttribute('data-filter-page', String(part.page));
      nav.appendChild(link);
    });

    if (currentPage < totalPages && state.paginationPartsTemplate?.next) {
      const next = document.createElement('div');
      next.innerHTML = state.paginationPartsTemplate.next;
      const nextWrapper = next.firstElementChild;

      if (nextWrapper) {
        const link = nextWrapper.querySelector('a') || nextWrapper;
        link.setAttribute('data-filter-page', String(currentPage + 1));
        link.setAttribute('href', '#');
        nav.appendChild(nextWrapper);
      }
    }

    container.innerHTML = '';
    container.appendChild(nav);
  }

  function updateProductCount(collection, count) {
    const countElement = collection.querySelector(SELECTORS.productCount);

    if (!countElement) {
      return;
    }

    const singularTemplate = countElement.dataset.countSingular || '@@count@@ product';
    const pluralTemplate = countElement.dataset.countPlural || '@@count@@ products';
    const template = count === 1 ? singularTemplate : pluralTemplate;

    countElement.textContent = template.replace('@@count@@', count);
  }

  function getFilterLabel(collection, state) {
    const labels = [];
    const tab = findTabByKey(collection, state.activeTabKey);

    if (tab && !isShowAllItem(tab)) {
      labels.push(getTabTitle(tab));
    }

    if (state.activeColorKey !== '__all__') {
      const colorTile = Array.from(collection.querySelectorAll(SELECTORS.colorTile))
        .find((tile) => getColorTileKey(tile) === state.activeColorKey);

      if (colorTile) {
        labels.push(getColorTileTitle(colorTile));
      }
    }

    return labels.join(' / ') || 'selection';
  }

  function updateEmptyState(collection, state, visibleCount) {
    const emptyState = collection.querySelector(SELECTORS.emptyState);
    const clearFilterChip = collection.querySelector(SELECTORS.clearFilterChip);

    if (!emptyState) {
      return;
    }

    const showEmpty = isCombinedFiltered(state) && visibleCount === 0;

    emptyState.hidden = !showEmpty;

    // Keep a single clear action: chip when products are visible, outline button in empty state.
    if (clearFilterChip) {
      clearFilterChip.hidden = !isCombinedFiltered(state) || showEmpty;
    }

    if (!showEmpty) {
      return;
    }

    const messageElement = emptyState.querySelector(SELECTORS.emptyMessage);
    const messageTemplate = emptyState.dataset.emptyMessage || 'No products found for @@tab@@.';

    if (messageElement) {
      messageElement.textContent = messageTemplate.replace('@@tab@@', getFilterLabel(collection, state));
    }
  }

  function scrollToCollection(collection) {
    // Scroll to the filter tabs/toolbar so they stay visible under the sticky header.
    const scrollTarget =
      collection.querySelector('.collection__toolbar') ||
      collection.querySelector('.collection-color-tiles') ||
      collection.querySelector(SELECTORS.results) ||
      collection;
    const isDesktop = window.matchMedia('screen and (min-width: 1000px)').matches;

    if (!isDesktop) {
      scrollTarget.scrollIntoView({ block: 'start', behavior: 'smooth' });
      return;
    }

    const topOffset = 100;
    const targetTop = scrollTarget.getBoundingClientRect().top + window.scrollY - topOffset;

    window.scrollTo({
      top: Math.max(0, targetTop),
      behavior: 'smooth',
    });
  }

  function syncFilterUi(collection, state) {
    setTabActiveState(collection, state.activeTabKey);
    setColorTileActiveState(collection, state.activeColorKey);

    const tab = findTabByKey(collection, state.activeTabKey);
    const mobileLabel = collection.querySelector('[data-collection-filter-mobile-label]');
    const clearFilterChip = collection.querySelector(SELECTORS.clearFilterChip);
    const emptyState = collection.querySelector(SELECTORS.emptyState);
    const showingEmpty = emptyState && !emptyState.hidden;

    if (mobileLabel && tab) {
      mobileLabel.textContent = getTabTitle(tab);
    }

    if (clearFilterChip) {
      clearFilterChip.hidden = !isCombinedFiltered(state) || showingEmpty;
    }
  }

  async function renderFilteredPage(collection, state) {
    const catalog = await preloadCatalog(collection, state);
    const productList = collection.querySelector(SELECTORS.productList);
    const emptyState = collection.querySelector(SELECTORS.emptyState);

    if (!productList || !catalog.length) {
      return;
    }

    const filteredProducts = getFilteredProducts(catalog, collection, state);
    const perPage = getPerPage(collection);

    if (filteredProducts.length === 0) {
      productList.innerHTML = '';
      renderClientPagination(collection, state, 1, 0);
      updateProductCount(collection, 0);
      updateEmptyState(collection, state, 0);
      return;
    }

    const totalPages = Math.max(1, Math.ceil(filteredProducts.length / perPage));

    state.currentPage = Math.min(Math.max(state.currentPage, 1), totalPages);

    const pageProducts = filteredProducts.slice((state.currentPage - 1) * perPage, state.currentPage * perPage);
    const handles = pageProducts.map((product) => product.handle);

    setLoading(collection, true);
    showSkeletons(productList, handles.length || Math.min(perPage, 8));

    if (emptyState) {
      emptyState.hidden = true;
    }

    try {
      const cards = await fetchCardsForHandles(collection, state, catalog, handles);

      productList.innerHTML = '';

      cards.forEach((card) => {
        productList.appendChild(document.importNode(card, true));
      });

      if (typeof productList.reveal === 'function') {
        productList.reveal();
      }

      renderClientPagination(collection, state, state.currentPage, totalPages);
      updateProductCount(collection, filteredProducts.length);
      updateEmptyState(collection, state, filteredProducts.length);
    } finally {
      setLoading(collection, false);
    }
  }

  async function renderAllTabPage(collection, state, page = 1) {
    const catalog = await preloadCatalog(collection, state);
    const results = collection.querySelector(SELECTORS.results);
    const paginationContainer = collection.querySelector(SELECTORS.paginationContainer);
    const emptyState = collection.querySelector(SELECTORS.emptyState);
    const existingColorTiles = collection.querySelector('.collection-color-tiles');

    if (!results) {
      return;
    }

    setLoading(collection, true);

    try {
      const { results: fetchedResults, pagination, colorTiles } = await fetchCollectionSectionPage(collection, page);

      if (fetchedResults) {
        results.innerHTML = fetchedResults.innerHTML;
      }

      if (paginationContainer) {
        paginationContainer.innerHTML = pagination ? pagination.outerHTML : '';
      }

      if (emptyState) {
        emptyState.hidden = true;
      }

      if (colorTiles && existingColorTiles) {
        existingColorTiles.outerHTML = colorTiles.outerHTML;
        enhanceColorTileScrollers(collection);
      }

      cacheInitialCards(collection, state);
      state.currentPage = page;
      syncFilterUi(collection, state);

      const totalProducts = parseInt(collection.dataset.totalProducts, 10) || catalog.length;
      updateProductCount(collection, totalProducts);

      const productList = collection.querySelector(SELECTORS.productList);

      if (productList && typeof productList.reveal === 'function') {
        productList.reveal();
      }
    } finally {
      setLoading(collection, false);
    }
  }

  async function renderCurrentView(collection, state, page = 1) {
    state.currentPage = page;
    syncFilterUi(collection, state);

    if (isCombinedFiltered(state)) {
      await renderFilteredPage(collection, state);
    } else {
      await renderAllTabPage(collection, state, page);
    }
  }

  async function applyTab(collection, state, tab, page = 1) {
    const tabKey = getTabKey(tab);
    const previousTabKey = state.activeTabKey;
    const wasFiltered = isCombinedFiltered(state);

    state.activeTabKey = tabKey;

    if (!isCombinedFiltered(state) && tabKey === '__all__' && state.activeColorKey === '__all__' && previousTabKey === '__all__' && page === 1 && !wasFiltered) {
      syncFilterUi(collection, state);
      return;
    }

    await renderCurrentView(collection, state, page);
    scrollToCollection(collection);
  }

  async function applyColorTile(collection, state, tile, page = 1) {
    state.activeColorKey = getColorTileKey(tile);
    state.currentPage = page;

    await renderCurrentView(collection, state, page);
    scrollToCollection(collection);
  }

  function bindDocumentClicks() {
    if (documentClicksBound) {
      return;
    }

    documentClicksBound = true;
    bindColorTilePointerTracking();

    document.addEventListener('click', async (event) => {
      const tab = event.target.closest(SELECTORS.tab);

      if (tab) {
        const collection = getCollectionForFilterControl(tab);

        if (!collection) {
          return;
        }

        event.preventDefault();
        closeFilterTabPopover(tab);
        await applyTab(collection, getState(collection), tab, 1);
        return;
      }

      const collection = event.target.closest(SELECTORS.section);

      if (!collection) {
        return;
      }

      const state = getState(collection);
      const colorTile = event.target.closest(SELECTORS.colorTile);

      if (colorTile && collection.contains(colorTile)) {
        if (isColorTileDragClick(event)) {
          event.preventDefault();
          resetColorTilePointer();
          return;
        }

        event.preventDefault();
        await applyColorTile(collection, state, colorTile, 1);
        return;
      }

      const pageLink = event.target.closest('[data-filter-page]');

      if (pageLink && collection.contains(pageLink) && isCombinedFiltered(state)) {
        event.preventDefault();
        const page = parseInt(pageLink.getAttribute('data-filter-page'), 10);

        if (!Number.isNaN(page)) {
          await renderCurrentView(collection, state, page);
          scrollToCollection(collection);
        }
        return;
      }

      const clearButton = event.target.closest(SELECTORS.clearFilter);

      if (clearButton && collection.contains(clearButton)) {
        event.preventDefault();
        await clearAllFilters(collection, state);
      }
    });
  }

  function bindShopifySection(shopifySection) {
    if (boundShopifySections.has(shopifySection)) {
      return;
    }

    boundShopifySections.add(shopifySection);
    bindDocumentClicks();
  }

  function initSection(collection) {
    const shopifySection = getShopifySection(collection);
    const state = getState(collection);

    bindShopifySection(shopifySection);
    cacheInitialCards(collection, state);
    storePaginationTemplate(collection, state);
    syncFilterUi(collection, state);
    enhanceColorTileScrollers(collection);
    enhanceFilterTabsPopovers(collection);
    preloadCatalog(collection, state);
  }

  function init() {
    document.querySelectorAll(SELECTORS.section).forEach((collection) => {
      initSection(collection);
    });
  }

  document.addEventListener('facet:update', () => {
    // Theme replaces section children after theme:loading:end in the same turn.
    // Re-bind on the next frame so we attach to the new color tile carousel.
    let settled = false;

    const rebindAfterFacetUpdate = () => {
      if (settled) {
        return;
      }

      settled = true;
      document.documentElement.removeEventListener('theme:loading:end', rebindAfterFacetUpdate);

      requestAnimationFrame(() => {
        document.querySelectorAll(SELECTORS.section).forEach((collection) => {
          const state = getState(collection);

          cacheInitialCards(collection, state);
          storePaginationTemplate(collection, state);
          syncFilterUi(collection, state);
          enhanceColorTileScrollers(collection);
          enhanceFilterTabsPopovers(collection);

          if (!isCombinedFiltered(state)) {
            const totalProducts = parseInt(collection.dataset.totalProducts, 10) || state.catalog?.length || 0;
            updateProductCount(collection, totalProducts);
          }
        });
      });
    };

    document.documentElement.addEventListener('theme:loading:end', rebindAfterFacetUpdate);

    // If the facet request aborts, loading:end may never fire — clear the listener.
    setTimeout(() => {
      if (!settled) {
        document.documentElement.removeEventListener('theme:loading:end', rebindAfterFacetUpdate);
      }
    }, 10000);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const collection = event.target.querySelector(SELECTORS.section);

    if (collection) {
      initSection(collection);
    }
  });
})();
